import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useGroupInfo } from '../hooks/queries/useGroupInfo';
import { useGroupMembers } from '../hooks/queries/useGroupMembers';
import { useEditGroup } from '../hooks/mutations/useEditGroup';
import { currencies } from '../constants/currencies';
import { XIcon } from 'lucide-react';
import Header from '../components/Header';
import toast from 'react-hot-toast';

interface EditGroupForm {
  groupName: string;
  currency: string;
  password: string;
}

interface MemberState {
  id: string;
  originalName: string;
  currentName: string;
  isModified: boolean;
}

interface AddedMember {
  id: string; // 임시 ID
  name: string;
}

export const EditGroupPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: group, isLoading: groupLoading } = useGroupInfo(id);
  const { data: members, isLoading: membersLoading } = useGroupMembers(id);
  const editGroupMutation = useEditGroup();

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<EditGroupForm>();
  
  const [originalMembers, setOriginalMembers] = useState<MemberState[]>([]);
  const [addedMembers, setAddedMembers] = useState<AddedMember[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoadingRef = useRef(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalGroupName, setOriginalGroupName] = useState('');
  const [originalPassword, setOriginalPassword] = useState('');
  const [originalTreasurerMemberId, setOriginalTreasurerMemberId] = useState<string | null>(null);
  const [treasurerMemberId, setTreasurerMemberId] = useState<string>('');

  // 폼 데이터 감시
  const watchedGroupName = watch('groupName');
  const watchedPassword = watch('password');

  // Enter 키 핸들러 - 폼 전송 방지하고 포커스 해제
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  // 그룹과 멤버 정보가 로드되면 초기값 설정
  useEffect(() => {
    if (group) {
      setValue('groupName', group.name);
      setValue('currency', group.default_currency);
      setValue('password', group.password || '');
      setOriginalGroupName(group.name);
      setOriginalPassword(group.password || '');
      setOriginalTreasurerMemberId(group.treasurer || null);
      setTreasurerMemberId(group.treasurer || '');
    }
  }, [group, setValue]);

  useEffect(() => {
    if (members) {
      const memberStates: MemberState[] = members.map(member => ({
        id: member.id,
        originalName: member.user_name,
        currentName: member.user_name,
        isModified: false
      }));
      setOriginalMembers(memberStates);
    }
  }, [members]);

  // 변경사항 실시간 감지
  useEffect(() => {
    if (!originalGroupName) return; // 초기 로딩 중이면 무시

    const groupNameChanged = watchedGroupName !== originalGroupName;
    const passwordChanged = watchedPassword !== originalPassword;
    const hasMemberChanges = originalMembers.some(member => member.isModified);
    const hasNewMembers = addedMembers.some(member => member.name.trim());
    const treasurerChanged = treasurerMemberId !== (originalTreasurerMemberId || '');

    setHasChanges(groupNameChanged || passwordChanged || hasMemberChanges || hasNewMembers || treasurerChanged);
  }, [watchedGroupName, originalGroupName, originalMembers, addedMembers, watchedPassword, originalPassword, treasurerMemberId, originalTreasurerMemberId]);

  // 로딩 중
  if (groupLoading || membersLoading) return null;

  // 그룹이 존재하지 않으면 리다이렉트
  if (!group) return <Navigate to="/groups" />;

  // 기존 멤버 이름 변경 핸들러
  const handleMemberNameChange = (memberId: string, newName: string) => {
    setOriginalMembers(prev => 
      prev.map(member => 
        member.id === memberId 
          ? { 
              ...member, 
              currentName: newName,
              isModified: newName !== member.originalName
            }
          : member
      )
    );
  };

  // 새 멤버 추가 핸들러
  const handleAddMember = () => {
    const newMember: AddedMember = {
      id: `temp_${Date.now()}`,
      name: ''
    };
    setAddedMembers(prev => [...prev, newMember]);
  };

  // 새 멤버 이름 변경 핸들러
  const handleAddedMemberNameChange = (memberId: string, newName: string) => {
    setAddedMembers(prev => 
      prev.map(member => 
        member.id === memberId 
          ? { ...member, name: newName }
          : member
      )
    );
  };

  // 새 멤버 삭제 핸들러
  const handleRemoveAddedMember = (memberId: string) => {
    setAddedMembers(prev => prev.filter(member => member.id !== memberId));
  };

  // 수정 사항 추적
  const getChanges = (formData: EditGroupForm) => {
    const changes: {
      groupName?: string;
      password?: string;
      membersToRename?: Array<{memberId: string, newName: string}>;
      membersToAdd?: string[];
      treasurerMemberId?: string | null;
    } = {};

    // 그룹 이름 변경 확인
    if (formData.groupName !== originalGroupName) {
      changes.groupName = formData.groupName;
    }

    // 비밀번호 변경 확인
    if (formData.password !== originalPassword) {
      changes.password = formData.password;
    }

    // 멤버 이름 변경 확인
    const modifiedMembers = originalMembers.filter(member => member.isModified && member.currentName.trim());
    if (modifiedMembers.length > 0) {
      changes.membersToRename = modifiedMembers.map(member => ({
        memberId: member.id,
        newName: member.currentName.trim()
      }));
    }

    // 새 멤버 추가 확인
    const validNewMembers = addedMembers.filter(member => member.name.trim());
    if (validNewMembers.length > 0) {
      changes.membersToAdd = validNewMembers.map(member => member.name.trim());
    }

    // 총무 변경/해제 확인
    if (treasurerMemberId !== (originalTreasurerMemberId || '')) {
      changes.treasurerMemberId = treasurerMemberId ? treasurerMemberId : null;
    }

    return changes;
  };

  // 폼 제출 핸들러
  const onSubmit = async (data: EditGroupForm) => {
    if (isLoadingRef.current) return;
    
    try {
      setIsSubmitting(true);
      isLoadingRef.current = true;

      // 빈 이름 체크
      const hasEmptyOriginalNames = originalMembers.some(member => !member.currentName.trim());
      const hasEmptyAddedNames = addedMembers.some(member => !member.name.trim());

      if (hasEmptyOriginalNames || hasEmptyAddedNames) {
        toast.error('멤버 이름은 비워둘 수 없습니다.');
        return;
      }

      const changes = getChanges(data);

      await editGroupMutation.mutateAsync({
        groupId: id!,
        ...changes
      });

      navigate(`/groups/${id}`, { replace: true });
    } catch (error) {
      console.error('Error editing group:', error);
      // 에러는 mutation의 onError에서 처리됨
    } finally {
      setIsSubmitting(false);
      isLoadingRef.current = false;
    }
  };

  return (
    <div className="container mx-auto px-4 w-full pt-20">
      <Header title="그룹 수정" showBackButton onBackClick={() => navigate(-1)} />
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        {/* 그룹 이름 */}
        <div className="mb-4 w-full">
          <label>
            그룹 이름
          </label>
          <div className="relative">
            <input
              {...register("groupName", { required: "그룹 이름을 입력해주세요" })}
              onKeyDown={handleKeyDown}
              className="w-full"
            />
            {watchedGroupName !== originalGroupName && watchedGroupName && (
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 text-sm">변경됨</span>
            )}
          </div>
          {errors.groupName && (
            <p className="text-red-500 text-sm mt-2 ml-2">{errors.groupName.message}</p>
          )}
        </div>

        {/* 정산 통화 (비활성화) */}
        <div className="mb-4 w-full">
          <label>
            정산 통화
          </label>
          <div className="relative">
            <select
              {...register("currency")}
              disabled
              className="w-full bg-gray-100 opacity-50"
            >
              {Object.entries(currencies).map(([code, { name }]) => (
                <option key={code} value={code}>
                  {`${name} (${code})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 비밀번호 */}
        <div className="mb-4 w-full">
          <label>
            입장 비밀번호 (선택)
          </label>
          <div className="relative">
            <input
              {...register("password", { minLength: {
                value: 4,
                message: '4자리 숫자로 입력해주세요'
              }, maxLength: {
                value: 4,
                message: '4자리 숫자로 입력해주세요'
              } })}
              type="number"
              inputMode='numeric'
              placeholder="4자리 숫자 입력 (선택)"
              pattern='[0-9]{4}'
              className="w-full"
              value={watchedPassword}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(/[^0-9]/g, '').slice(0,4);
                setValue('password', filteredValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
            {watchedPassword !== originalPassword && (
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 text-sm">변경됨</span>
            )}
          </div>
          {errors.password && (
            <p className="text-red-500 text-sm mt-2 ml-2">{errors.password.message}</p>
          )}
        </div>

        {/* 총무 변경/해제 */}
        {originalMembers.length >= 3 && (
          <div className="mb-4 w-full">
            <label>
              총무 변경
            </label>
            <div className="relative">
              <select
                value={treasurerMemberId}
                onChange={(e) => setTreasurerMemberId(e.target.value)}
                className="w-full text-blue-500"
              >
                <option value="">총무 없음(기본값)</option>
                {originalMembers
                  .filter((m) => m.currentName.trim())
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      총무: {m.currentName}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        )}


        {/* 멤버 목록 */}
        <div className="mb-4 w-full">
          <label>
            멤버 이름변경
          </label>
          
          {/* 기존 멤버들 */}
          <div className="space-y-2">
            {originalMembers.map((member) => (
              <div key={member.id} className="flex items-center relative">
                <input
                  type="text"
                  value={member.currentName}
                  onChange={(e) => handleMemberNameChange(member.id, e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1"
                  placeholder="멤버 이름"
                />
                {member.isModified && (
                  <span className="absolute right-3 text-blue-500 text-sm">변경됨</span>
                )}
              </div>
            ))}
            {addedMembers.length > 0 && (
                <>
                {addedMembers.map((member) => (
                  <div key={member.id} className="flex items-center relative">
                    <input
                      type="text"
                      value={member.name}
                      onChange={(e) => handleAddedMemberNameChange(member.id, e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="flex-1"
                      placeholder="새 멤버 이름"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveAddedMember(member.id)}
                      className="text-gray-500 hover:text-gray-700 rounded-full p-1 bg-gray-100 absolute right-3"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                </>
            )}
          </div>
          {/* 멤버 추가 버튼 */}
          <button
            type="button"
            onClick={handleAddMember}
            className="w-full text-left h-12 text-blue-500 text"
          >
            + 멤버 추가하기
          </button>
        </div>

        {/* 수정 버튼 */}
        <div className="flex items-center justify-between w-full mt-8">
          <button
            type="submit"
            disabled={!hasChanges || isSubmitting || editGroupMutation.isPending}
            className="w-full bg-blue-500 text-white text-lg font-semibold py-3.5 px-4 rounded-xl"
          >
            {isSubmitting || editGroupMutation.isPending ? '처리 중...' : '수정하기'}
          </button>
        </div>
      </form>
    </div>
  );
};
